import { useState, useCallback } from 'react';

interface CodeBlockProps {
  language: string;
  code: string;
  filename?: string;
}

export default function CodeBlock({ language, code, filename }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [code]);

  return (
    <div className="overflow-hidden rounded-xl bg-slate-900 shadow-xl border border-slate-800 my-6">
      <div className="flex items-center justify-between bg-slate-800/50 px-4 py-2 text-xs text-slate-400">
        <div className="flex gap-2 items-center">
          <span className="uppercase font-medium tracking-wider">{language}</span>
          {filename && (
            <>
              <span className="text-slate-600" aria-hidden="true">·</span>
              <span className="font-medium italic">{filename}</span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className={`flex items-center gap-1.5 transition-all duration-200 focus:outline-none ${
            copied ? 'text-emerald-400' : 'hover:text-white'
          }`}
          aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
        >
          {copied ? (
            <>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-bold uppercase tracking-tighter">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2" />
              </svg>
              <span className="font-medium uppercase tracking-tighter">Copy</span>
            </>
          )}
        </button>
      </div>
      <div className="p-4 overflow-x-auto custom-scrollbar">
        <pre className="text-sm font-mono text-emerald-400 leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
}
